import { ClipboardCheck, NotebookPen, UserCheck } from 'lucide-react';
import { RealScreen } from '@/components/marketing/roleKit';

/**
 * Parents Real Screen 2: progress. The real parent view is the Learning Progress tab: the tutor's
 * written feedback per completed session (attendance, topics, notes, homework, areas to improve,
 * progress summary). There are no skill percentages or badges in the parent view, so none appear here.
 */
function Row({ label, body }: { label: string; body: string }) {
  return (
    <div>
      <p className="text-xs font-semibold">{label}</p>
      <p className="mt-0.5 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

export function ParentProgressSection() {
  return (
    <RealScreen
      id="section-parent-progress"
      audience="parents"
      layout="below"
      heading="Progress, in the tutor's own words"
      lede="After each session the tutor writes up what happened. You read it in the Learning Progress tab."
      callouts={[
        { icon: NotebookPen, title: 'Written after every session', body: 'Topics covered, notes, and a short summary of progress.' },
        { icon: UserCheck, title: 'Attendance is recorded', body: 'A missed session shows as not attended, with the tutor’s note.' },
        { icon: ClipboardCheck, title: 'Homework and next focus', body: 'You see what was set and what the next session will work on.' },
      ]}
      caption="Example tutor feedback."
    >
      <div className="mx-auto max-w-3xl rounded-xl border bg-background">
        <div className="flex items-center justify-between rounded-t-xl border-b bg-emerald-600/10 px-5 py-3">
          <p className="text-sm font-semibold">Tutor feedback · Algebra</p>
          <span className="rounded-full bg-emerald-600 px-2.5 py-0.5 text-xs font-medium text-white">Attended</span>
        </div>
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <Row label="Topics covered" body="Quadratic equations: factoring and the quadratic formula." />
          <Row label="Notes" body="Alex did great work today. Word problems are still slower; we will spend more time on them." />
          <Row label="Homework" body="Six factoring problems and two word problems." />
          <Row label="Areas for improvement" body="Setting up the equation from a sentence." />
          <div className="sm:col-span-2">
            <Row label="Progress" body="Noticeably quicker at choosing a method before starting." />
          </div>
        </div>
      </div>
    </RealScreen>
  );
}
